<template>
  <div class="setting-container">
    <div class="content-wrapper" v-if="!isLoadingData">
      <div class="wizard-form">
        <div class="ncm-row" v-if="hasSourceTemplate">
          <label class="ncm-column-left">
            {{ $t("sourcePageTemplate") }}
          </label>
          <div class="ncm-column-right m-name">
            <div>{{ sourceTemplate.name }}</div>
          </div>
        </div>

        <div class="ncm-row" v-if="currentApp.isTemplate">
          <label class="ncm-column-left">
            {{ $t("pageTemplateVersion") }}
          </label>
          <div class="ncm-column-right m-name">
            <div>
              <b-form-select
                @change="onTemplateVersionChange"
                v-model="selectedTemplateVersion"
                :options="dpVersions"
                size="sm"
              ></b-form-select>
            </div>
          </div>
        </div>

        <div class="ncm-row">
          <label class="ncm-column-left" for="setting-app-name">
            {{ nameLabel }}
          </label>
          <div class="ncm-column-right m-name">
            <text-input
              id="setting-app-name"
              v-model="dpAppName"
              type="text"
              :focus="true"
              @on-text-input-change="onAppNameInputChanged"
            />
          </div>
        </div>

        <element-description
          :label="descriptionText"
          :outer-container-class="'ncm-row'"
          :label-class="'ncm-column-left'"
          :value-container-class="'ncm-column-right'"
          :elem-description="currentApp.description"
          @on-element-description-changed="onDeDescriptionChanged"
        ></element-description>

        <div class="ncm-row">
          <label class="ncm-column-left">
            {{ typeLabel }}
          </label>
          <section class="ncm-column-right">
            <input
              class="content-type"
              type="text"
              :value="typeText"
              disabled
            />
          </section>
        </div>

        <div class="ncm-row" v-if="!currentApp.customContent && searchable">
          <label class="ncm-column-left">
            {{ searchBoxLabel }}
          </label>
          <div class="ncm-column-right m-name">
            <search-box
              ref="searchInputInCreate"
              :has-icon="false"
              :box-type="'createApp'"
              :list="searchResults"
              :value="dpTestRecord"
              :label-only="isLabelOnly(currentApp.type)"
              @on-search-box-item-selected="onSearchBoxItemSelected"
              @on-search-box-change="onSearchBoxChange"
            />
          </div>
        </div>
      </div>

      <div class="btn-row">
        <div class="btn-wrapper">
          <div class="loader" v-if="isSaving"></div>

          <button
            v-if="
              !currentApp.customContent && !currentApp.isTemplate && !isSaving
            "
            @click="convertToTemplateConfirm"
            class="slds-button slds-button_outline-brand"
          >
            {{ $t("convertToPageTemplate") }}
          </button>

          <button
            @click="updateSourceTemplateConfirm"
            v-if="hasSourceTemplate && !isSaving"
            class="slds-button slds-button_outline-brand"
          >
            {{ $t("updateSourceTemplate") }}
          </button>

          <button
            v-if="!isSaving"
            class="slds-button slds-button_brand"
            @click="saveForm()"
          >
            {{ getMessageText(messages.SAVE) }}
          </button>
        </div>
      </div>
    </div>
    <div class="loader" v-if="isLoadingData"></div>
  </div>
</template>

<script>
  import { mapState } from "vuex";
  import {
    APP_TYPES_TO_OBJECT_RECORD_TYPES_MAPPING,
    CREATE_WIZARD_SEARCH_MAP,
    CREATE_WIZARD_SOSL_SEARCH_MAP,
    MUTATION_NAME,
    RICH_RESULT_CONTENT_TYPE,
    TOAST_CONFIG
  } from "@/static/global-variables";
  import SearchBox from "@/components/SearchBox";
  import TextInput from "@/components/TextInput";
  import services from "@/services";
  import utils from "@/utils";
  import store from "@/store";
  import debounce from "lodash.debounce";
  import { DateTime } from "luxon";
  import templateService from "@/services/templates-service";
  import ElementDescription from "@/components/ElementDescription";
  import { QUERIES } from "@/static/queries";
  import territoryFeedbackLogin from "@/services/territory-feedback/territoryFeedbackLogin";

  function prepareTestRecord(component) {
    if (component.isTerritoryFeedbackContentType()) {
      component.searchBoxLabel = utils.getVeevaMessageFromTemplate(
        component.getMessageText(component.messages.TEST_RECORD),
        component.$t("modelTerritory"),
        "Test " // need to be default English
      );
      const handleResponse = resp => {
        if (!resp || resp.isManager) {
          component.displayErrorMessage(resp);
        } else if (resp.tree && resp.tree.root) {
          const modelTerr = territoryFeedbackLogin.getModelNameFromId(
            resp.tree.root,
            component.currentApp.testRecord
          );
          if (modelTerr.length > 0) {
            component.originTestRecord = modelTerr[0];
            component.dpTestRecord = JSON.parse(
              JSON.stringify(component.originTestRecord)
            );
          }
        }
      };
      territoryFeedbackLogin
        .territoryFeedbackLogin(component.user.id)
        .then(resp => handleResponse(resp));

      component.searchable = true;
    } else {
      let labels = component.objectNameLabels;
      const mapping = utils.getRecordTypeObject(component.currentApp.type);
      if (mapping) {
        component.searchBoxLabel = utils.getVeevaMessageFromTemplate(
          component.getMessageText(component.messages.TEST_RECORD),
          labels[mapping.object].singular,
          "Test " // need to be default English
        );

        services
          .fetchTestRecordInfo(mapping, component.currentApp.testRecord)
          .then(resp => {
            if (resp.length > 0) {
              component.originTestRecord = resp[0];
              component.dpTestRecord = JSON.parse(
                JSON.stringify(component.originTestRecord)
              );
            }
          });

        component.searchable = true;
      } else {
        component.searchable = false;
      }
    }
  }

  export default {
    name: "Settings",
    props: {
      appId: String
    },
    components: {
      SearchBox,
      TextInput,
      ElementDescription
    },
    data() {
      return {
        dpAppName: "",
        dpAppType: "",
        dpVersions: [],
        originTestRecord: null,
        dpTestRecord: null,
        isMetaLoading: true,
        isFetchingVersions: false,
        selectedTemplateVersion: null,
        testRecord: "",
        searchBoxLabel: "",
        searchable: false,
        types: [],
        searchResults: [],
        isSaving: false,
        dpAppDescription: null
      };
    },
    mounted() {
      this.dpAppName = this.currentApp.name;
      this.dpAppDescription = this.currentApp.description;
      this.fetchInitData();
    },
    computed: {
      ...mapState({
        apps: "apps",
        messages: "messages",
        user: "user",
        userLocale: "userLocale",
        appTypes: "appTypes",
        objectNameLabels: "objectNameLabels"
      }),
      currentApp() {
        return utils.findAppByAppId(this.apps, this.appId);
      },
      formattedUserLocale() {
        return utils.getStandardLocalFormat(this.userLocale);
      },
      isLoadingData() {
        return this.isMetaLoading || this.isFetchingVersions;
      },
      templates() {
        return this.apps.filter(a => !!a.isTemplate);
      },
      sourceTemplate() {
        return utils.findAppByAppId(
          this.apps,
          this.currentApp.sourceTemplateUuid
        );
      },
      hasSourceTemplate() {
        return !!this.sourceTemplate;
      },
      cancelText() {
        return this.getMessageText(this.messages.CANCEL);
      },
      okText() {
        return this.getMessageText(this.messages.OK);
      },
      descriptionText() {
        return this.getMessageText(this.messages.DESCRIPTION);
      },
      nameLabel() {
        if (this.currentApp.isTemplate) {
          return this.$t("pageTemplateName");
        } else {
          return this.$t("pageName");
        }
      },
      typeLabel() {
        if (this.currentApp.isTemplate) {
          return this.$t("pageTemplateType");
        } else {
          return this.$t("pageType");
        }
      },
      typeText() {
        return this.$t(utils.camelCase(this.currentApp.type));
      }
    },
    methods: {
      onDeDescriptionChanged(input) {
        this.dpAppDescription = input;
      },
      displayErrorMessage(message) {
        this.$bvToast.toast(message, TOAST_CONFIG.BOTTOM_CENTER_ERROR);
      },
      onTemplateVersionChange(val) {
        this.selectedTemplateVersion = val;
      },
      performSearch(input) {
        const message = this.getMessageText(this.messages.SFDC_DATA_ERROR);
        if (this.isTerritoryFeedbackContentType()) {
          territoryFeedbackLogin.territoryFeedbackLogin(this.user.id).then(
            resp => {
              if (!resp || resp.isManager) {
                this.displayErrorMessage(message);
              } else if (resp.tree && resp.tree.root) {
                this.searchResults = territoryFeedbackLogin.formatFeedbackLoginResults(
                  resp.tree.root
                );
              }
            },
            () => {
              this.displayErrorMessage(message);
            }
          );
        } else {
          services
            .findRecordsForObject(
              CREATE_WIZARD_SOSL_SEARCH_MAP[
                this.currentApp.type.replace(/ /g, "_")
              ],
              input
            )
            .then(
              resp => {
                if (!resp || !Array.isArray(resp.searchRecords)) {
                  this.displayErrorMessage(message);
                } else {
                  this.searchResults = resp.searchRecords;
                }
              },
              () => {
                this.displayErrorMessage(message);
              }
            );
        }
      },
      onSearchBoxChange: debounce(function(input) {
        this.performSearch(input);
      }, 1000),
      onSearchBoxItemSelected(selectedItem) {
        this.dpTestRecord = selectedItem;
      },
      onAppNameInputChanged(val) {
        this.dpAppName = val;
      },
      fetchInitData() {
        const pool = [];
        if (!this.objectNameLabels) {
          pool.push(services.fetchObjectNames());
        }

        if (!this.appTypes) {
          pool.push(services.fetchAppTypes());
        } else {
          this.types = this.appTypes;
        }

        if (this.currentApp.isTemplate) {
          this.isFetchingVersions = true;
          let component = this;
          services
            .fetchVersions(this.currentApp.uuid)
            .then(resp => {
              if (!Array.isArray(resp)) {
                component.isFetchingVersions = false;
                return;
              }
              component.dpVersions = resp
                .filter(res => {
                  return res.deployable;
                })
                .sort((a, b) => a - b)
                .map(res => {
                  let localTime = DateTime.fromISO(res.created)
                    .setLocale(this.formattedUserLocale)
                    .toLocaleString(DateTime.DATETIME_SHORT);

                  res.value = res.version;
                  res.text = `${res.version} (${localTime})`;

                  return res;
                });

              component.onTemplateVersionChange(component.currentApp.version);
              component.isFetchingVersions = false;
            })
            .catch(err => {
              console.error(err);
            });
        }

        if (pool.length > 0) {
          Promise.all(pool)
            .then(responses => {
              const result = {
                Account: responses[0].Account,
                KOL: responses[0].Account,
                Account_Plan_vod__c: responses[0].Account_Plan_vod__c,
                Order_vod__c: responses[0].Order_vod__c,
                Inventory_Monitoring_vod__c:
                  responses[0].Inventory_Monitoring_vod__c,
                EM_Speaker_vod__c: responses[0].EM_Speaker_vod__c,
                Call2_vod__c: responses[0].Call2_vod__c
              };

              store.commit(MUTATION_NAME.SET_OBJECT_NAME_LABELS, result);

              services.fetchObjectOLS().then(describeObjects => {
                if (describeObjects) {
                  const objectMap = new Map(Object.entries(result));
                  for (let label of objectMap.keys()) {
                    if (
                      !describeObjects[label] ||
                      !describeObjects[label].createable
                    )
                      objectMap.delete(label);
                  }

                  const recordTypes = utils.reformatRecordTypes(
                    responses[1].records,
                    objectMap
                  );

                  this.types = utils
                    .filterTypes(
                      recordTypes,
                      describeObjects[QUERIES.htmlReport.object]
                    )
                    .map(function(element) {
                      element.text = element.value.type;
                      return element;
                    }, this);

                  store.commit(MUTATION_NAME.SET_APPTYPES, this.types);

                  prepareTestRecord(this);
                  this.isMetaLoading = false;
                } else {
                  this.types = [];
                  store.commit(MUTATION_NAME.SET_APPTYPES, []);
                  this.isMetaLoading = false;
                }
              });
            })
            .catch(error => {
              console.error(error);

              store.commit(MUTATION_NAME.SET_OBJECT_NAME_LABELS, {});
              this.types = [];
              store.commit(MUTATION_NAME.SET_APPTYPES, []);
              this.isMetaLoading = false;
            });
        } else {
          prepareTestRecord(this);

          this.isMetaLoading = false;
        }
      },
      isTerritoryFeedbackContentType() {
        return (
          this.currentApp.type ===
          CREATE_WIZARD_SEARCH_MAP.Territory_Feedback_vod.contentType
        );
      },
      showConfirmModal() {
        return this.$bvModal.msgBoxConfirm(
          this.getMessageText(this.messages.ARE_YOU_SURE),
          {
            size: "sm",
            buttonSize: "sm",
            okVariant: "danger",
            okTitle: this.okText,
            cancelTitle: this.cancelText,
            footerClass: "p-2",
            hideHeaderClose: false,
            centered: true
          }
        );
      },
      convertToTemplateConfirm() {
        const component = this;
        this.showConfirmModal()
          .then(value => {
            if (value) {
              component.isSaving = true;
              let params = {
                name: component.currentApp.name,
                description: component.currentApp.description,
                appId: component.currentApp.uuid,
                record: component.currentApp.testRecord,
                isTemplate: true,
                sourceTemplateUuid: component.currentApp.sourceTemplateUuid,
                sourceTemplateVersion:
                  component.currentApp.sourceTemplateVersion
              };

              services.updateAppSetting(params).then(
                () => {
                  component.$router.go();
                },
                () => {
                  component.isSaving = false;
                  component.displayErrorMessage(
                    component.getMessageText(component.messages.SAVE_ERROR)
                  );
                }
              );
            }
          })
          .catch(console.error);
      },
      updateSourceTemplateConfirm() {
        const component = this;
        this.showConfirmModal()
          .then(value => {
            if (value) {
              component.isSaving = true;
              let params = {
                name: component.currentApp.name,
                description: component.currentApp.description,
                appId: component.currentApp.uuid,
                record: component.currentApp.testRecord
              };
              const errorMessage = component.getMessageText(
                component.messages.SAVE_ERROR
              );

              templateService
                .updateWithFile(
                  component.currentApp.sourceTemplateUuid,
                  null,
                  params
                )
                .then(
                  resp => {
                    if (!resp) {
                      component.displayErrorMessage(errorMessage);
                      return;
                    }

                    const app = JSON.parse(
                      JSON.stringify(component.sourceTemplate)
                    );
                    app.version = resp.version;
                    store.commit(MUTATION_NAME.UPDATE_APP, app);
                    component.$bvToast.toast(
                      component.getMessageText(component.messages.SAVE_SUCCESS),
                      TOAST_CONFIG.BOTTOM_CENTER_SUCCESS
                    );
                  },
                  () => {
                    component.displayErrorMessage(errorMessage);
                  }
                )
                .finally(() => {
                  component.isSaving = false;
                });
            }
          })
          .catch(console.error);
      },
      saveForm() {
        this.isSaving = true;
        let version = null;
        let clone = JSON.parse(JSON.stringify(this.currentApp));
        let testRecord = this.dpTestRecord ? this.dpTestRecord.Id : "";
        clone.name = this.dpAppName;
        clone.description = this.dpAppDescription;
        clone.testRecord = testRecord;
        if (clone.isTemplate) {
          version = this.selectedTemplateVersion;
          clone.version = version;
        }
        store.commit(MUTATION_NAME.UPDATE_APP, clone);

        const params = {
          name: this.dpAppName,
          description: this.dpAppDescription,
          appId: this.appId,
          record: this.dpTestRecord,
          isTemplate: clone.isTemplate,
          version
        };

        services
          .updateAppSetting(params)
          .then(
            () => {
              this.isSaving = false;
            },
            () => {
              this.isSaving = false;
              this.displayErrorMessage(
                this.getMessageText(this.messages.SAVE_ERROR)
              );
            }
          )
          .then(() => {
            if (this.isTerritoryFeedbackContentType()) {
              const objInfo = utils.getBridgeObjectIds(
                this.currentApp.type,
                store.getters.user.id,
                testRecord,
                null
              );
              services.instantiateBridge(objInfo);
            } else {
              services
                .fetchContentContextData(
                  this.currentApp.type,
                  this.currentApp.testRecord
                )
                .then(
                  resp => {
                    const r = resp.records[0] || {};
                    this.currentApp.contextAccount =
                      this.currentApp.type === "Account"
                        ? r.Id
                        : r.Account_vod__c;
                  },
                  err => {
                    console.error(err);
                  }
                )
                .then(() => {
                  const objInfo = utils.getBridgeObjectIds(
                    this.currentApp.type,
                    store.getters.user.id,
                    this.currentApp.testRecord,
                    this.currentApp.contextAccount
                  );
                  services.instantiateBridge(objInfo);
                });
            }
          })
          .catch(console.error);
      },
      isLabelOnly(type) {
        return !RICH_RESULT_CONTENT_TYPE.includes(
          APP_TYPES_TO_OBJECT_RECORD_TYPES_MAPPING[type]
        );
      }
    }
  };
</script>

<style scoped>
  .setting-container {
    padding: 2rem;
  }

  .content-wrapper {
    width: 50%;
    margin: auto;
  }

  .content-type {
    padding: 0 1rem 0 0.75rem;
    height: 32px;
  }

  .btn-row {
    display: flex;
  }

  .btn-wrapper {
    margin-left: auto;
    margin-top: 1rem;
  }
</style>